import { useState } from 'react';
import { TrashIcon, TruckIcon } from '@heroicons/react/24/outline';
import {
    useCreateOrderMethod,
    useDeleteOrderMethod,
    useUpdateOrderMethod
} from '../../../hooks/useVendors';
import { useToast } from '../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../utils/apiError';
import {
    MAX_VENDOR_ID_LENGTH,
    MAX_VENDOR_NAME_LENGTH,
    VENDOR_ID_PATTERN,
    type OrderMethodDetail,
    type OrderMethodKind
} from '../../../services/vendors';
import { ModalField, MODAL_INPUT_CLASSES } from '../../shared/ModalField';
import { ModalShell } from '../../shared/ModalShell';
import { VendorRecipientsField } from './VendorRecipientsField';
import { deleteMethodAffordance } from './deleteMethodAffordance';
import { SectionToggle } from '../../patient-files/actions/SectionToggle';

const KINDS: { value: OrderMethodKind; label: string; hint: string }[] = [
    { value: 'email', label: 'Email', hint: 'A purchase order is emailed to the addresses below.' },
    {
        value: 'external',
        label: 'Vendor portal',
        hint: "Ordered on the vendor's own website; nothing is emailed."
    }
];

function slugify(name: string): string {
    return name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .slice(0, MAX_VENDOR_ID_LENGTH)
        .replace(/^-+|-+$/g, '');
}

interface OrderMethodModalProps {
    vendorId: string;
    /** Omitted for a new method; `kind` is only selectable at creation. */
    method?: OrderMethodDetail;
    onClose: () => void;
}

export function OrderMethodModal({ vendorId, method, onClose }: OrderMethodModalProps) {
    const { showToast } = useToast();
    const createMethod = useCreateOrderMethod();
    const updateMethod = useUpdateOrderMethod();
    const deleteMethod = useDeleteOrderMethod();
    const isEditing = method !== undefined;
    const isPending = createMethod.isPending || updateMethod.isPending || deleteMethod.isPending;

    const [name, setName] = useState(method?.name ?? '');
    const [id, setId] = useState(method?.id ?? '');
    const [idEdited, setIdEdited] = useState(isEditing);
    const [kind, setKind] = useState<OrderMethodKind>(method?.kind ?? 'email');
    const [recipients, setRecipients] = useState<string[]>(method?.poRecipients ?? []);
    const [isActive, setIsActive] = useState(method?.isActive ?? true);

    const trimmedName = name.trim();
    const nameValid = trimmedName.length > 0 && trimmedName.length <= MAX_VENDOR_NAME_LENGTH;
    const effectiveId = idEdited ? id.trim() : slugify(name);
    const idValid = isEditing || VENDOR_ID_PATTERN.test(effectiveId);
    // Recipients exist only for email methods - a DB CHECK forbids them on an
    // external one, so the field is absent rather than disabled.
    const showRecipients = (isEditing ? method.kind : kind) === 'email';
    // Retiring a pre-rule method that never had an inbox must stay possible:
    // the server only rejects poRecipients a caller explicitly empties.
    const recipientsValid = !showRecipients || recipients.length > 0 || (isEditing && !isActive);
    const canSubmit = nameValid && idValid && recipientsValid && !isPending;

    const submit = async () => {
        try {
            if (isEditing) {
                await updateMethod.mutateAsync({
                    vendorId,
                    methodId: method.id,
                    // `kind` is immutable server-side, so it is never sent.
                    body: {
                        name: trimmedName,
                        ...(method.kind === 'email' && { poRecipients: recipients }),
                        isActive
                    }
                });
            } else {
                await createMethod.mutateAsync({
                    vendorId,
                    body: {
                        id: effectiveId,
                        name: trimmedName,
                        kind,
                        ...(kind === 'email' && { poRecipients: recipients }),
                        isActive
                    }
                });
            }
            showToast(isEditing ? 'Ordering method saved' : 'Ordering method added', 'success');
            onClose();
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to save the ordering method'), 'error');
        }
    };

    const remove = async () => {
        if (!method) return;
        try {
            await deleteMethod.mutateAsync({ vendorId, methodId: method.id });
            showToast('Ordering method deleted', 'success');
            onClose();
        } catch (error) {
            // 409 names the exact blocking counts - show it verbatim.
            showToast(apiErrorMessage(error, 'Failed to delete the ordering method'), 'error');
        }
    };

    const affordance = method ? deleteMethodAffordance(method) : { enabled: false, hint: '' };

    return (
        <ModalShell
            widthClassName="max-w-[480px]"
            panelClassName="rounded-xl max-h-[88vh] overflow-y-auto p-6"
            dismissible={!isPending}
            onDismiss={onClose}
            labelledBy="order-method-title"
        >
            <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                    <TruckIcon className="h-5 w-5 text-gray-600" />
                </span>
                <div>
                    <h2 id="order-method-title" className="text-lg font-bold text-gray-800">
                        {isEditing ? 'Edit ordering method' : 'New ordering method'}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                        {isEditing
                            ? 'Changing channel means adding a new method: products and past purchase orders were assigned on the old one.'
                            : 'How this vendor accepts orders for the products assigned to it.'}
                    </p>
                </div>
            </div>

            <div className="mt-5 flex flex-col gap-4">
                <ModalField htmlFor="method-name" label="Name" required>
                    <input
                        id="method-name"
                        type="text"
                        value={name}
                        autoFocus
                        maxLength={MAX_VENDOR_NAME_LENGTH}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Aligners by email"
                        className={MODAL_INPUT_CLASSES}
                    />
                </ModalField>

                {!isEditing && (
                    <ModalField
                        htmlFor="method-id"
                        label="Slug"
                        required
                        hintTone={idValid || effectiveId.length === 0 ? 'muted' : 'error'}
                        hint={
                            effectiveId.length > 0 && !idValid
                                ? 'Lowercase letters, digits and single hyphens only.'
                                : 'Permanent, and unique within this vendor.'
                        }
                    >
                        <input
                            id="method-id"
                            type="text"
                            value={effectiveId}
                            maxLength={MAX_VENDOR_ID_LENGTH}
                            onChange={(e) => {
                                setIdEdited(true);
                                setId(e.target.value);
                            }}
                            className={`${MODAL_INPUT_CLASSES} font-mono`}
                        />
                    </ModalField>
                )}

                {isEditing ? (
                    <div>
                        <span className="block text-xs font-semibold text-gray-700 mb-1">
                            Channel
                        </span>
                        <p className="text-sm text-gray-700">
                            {method.kind === 'email' ? 'Email' : 'Vendor portal'}
                            <span className="block text-xs text-gray-400 mt-0.5">
                                Fixed once created. Add a separate method for the other channel.
                            </span>
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-2">
                        <span className="block text-xs font-semibold text-gray-700">Channel *</span>
                        {KINDS.map((option) => (
                            <label
                                key={option.value}
                                className="flex items-start gap-2.5 cursor-pointer"
                            >
                                <input
                                    type="radio"
                                    name="method-kind"
                                    checked={kind === option.value}
                                    onChange={() => setKind(option.value)}
                                    className="mt-0.5 h-4 w-4 border-gray-300"
                                />
                                <span className="text-sm text-gray-700">
                                    {option.label}
                                    <span className="block text-xs text-gray-400 mt-0.5">
                                        {option.hint}
                                    </span>
                                </span>
                            </label>
                        ))}
                    </div>
                )}

                {showRecipients && (
                    <div>
                        <span className="block text-xs font-semibold text-gray-700 mb-1">
                            Purchase order recipients *
                        </span>
                        <VendorRecipientsField
                            recipients={recipients}
                            disabled={isPending}
                            onChange={setRecipients}
                        />
                    </div>
                )}

                <div className="flex items-start justify-between gap-3">
                    <label htmlFor="method-active" className="text-sm text-gray-700">
                        Active
                        <span className="block text-xs text-gray-400 mt-0.5">
                            Retiring a method stops ordering through it. Methods are never deleted.
                        </span>
                    </label>
                    <span className="flex h-5 items-center shrink-0">
                        <SectionToggle
                            id="method-active"
                            enabled={isActive}
                            disabled={isPending}
                            onToggle={() => setIsActive((prev) => !prev)}
                            size="sm"
                        />
                    </span>
                </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
                {isEditing && (
                    <button
                        type="button"
                        onClick={() => {
                            void remove();
                        }}
                        disabled={isPending || !affordance.enabled}
                        title={affordance.hint}
                        className="flex items-center gap-1.5 text-red-600 font-semibold px-3 py-2 rounded-lg text-sm hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                        <TrashIcon className="h-4 w-4" />
                        {deleteMethod.isPending ? 'Deleting…' : 'Delete'}
                    </button>
                )}
                <button
                    type="button"
                    onClick={onClose}
                    disabled={isPending}
                    className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50 ml-auto"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={() => {
                        void submit();
                    }}
                    disabled={!canSubmit}
                    className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isPending ? 'Saving…' : isEditing ? 'Save method' : 'Add method'}
                </button>
            </div>
        </ModalShell>
    );
}
